import React, { useState, useEffect } from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import { RiCloseLine } from 'react-icons/ri';
import { TbPlane } from 'react-icons/tb';
import * as translator from '../../utils/translator';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';
import useIsMobile from '../../customHooks/useIsMobile';

interface PharmacyCardProps {
  pharmacyId: string | null;
  pharmacy: {
    name: string;
    address: string;
    location?: { lat: number; lng: number };
    is_external_pharmacy: boolean;
    external_integration_code?: string;
  } | null;
  onCancel: () => void;
}

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const PharmacyCard: React.FC<PharmacyCardProps> = ({
  pharmacyId,
  pharmacy,
  onCancel,
}) => {
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const language = localStorage.getItem("language") || "de";
  const { customerState } = useCustomer();
  const { selectedService } = usePharmacyPickerContext();
  const isMobile = useIsMobile();
  
  useEffect(() => {
    if (pharmacyId && pharmacy?.location && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          // Silently fail - distance just won't be shown
          window.clientLogger?.debug?.(
            'Could not get user location for distance calculation',
            error
          );
        }
      );
    }
  }, [pharmacyId, pharmacy?.location]);

  if (!pharmacyId || !pharmacy) {
    return null;
  }

  // Calculate distance if user location and pharmacy location are available
  let distance: number | null = null;
  if (userLocation && pharmacy.location) {
    distance = calculateDistance(
      userLocation.lat,
      userLocation.lng,
      pharmacy.location.lat,
      pharmacy.location.lng
    );
  }

  // Determine which badge to show - only in prescription_only service
  const isOnlyPrescriptionService = selectedService === "prescription_only";
  const showUnknownStockBadge = isOnlyPrescriptionService && pharmacy.is_external_pharmacy && !pharmacy.external_integration_code;
  const showPayOnceBadge = isOnlyPrescriptionService && pharmacy.is_external_pharmacy === false;

  return (
    <div
      id="pharmacy-card"
      className={`w-full rounded-[10px] bg-[#F7FAEF] px-4 py-3 ${isMobile ? 'flex flex-row flex-nowrap items-start gap-3 min-w-0' : 'flex items-center justify-between'}`}
      style={{ borderColor: '#0A9281', borderWidth: '1px', borderStyle: 'solid' }}
    >
      {/* Left column: name, address, distance – on mobile stays in one column with min-w-0 so right column stays visible */}
      <div className={`${isMobile ? 'min-w-0 flex-1' : 'flex-1'}`}>
        <div id="pharmacy-card-name" className={`font-semibold text-[#072B26] ${isMobile ? 'text-sm line-clamp-2' : 'text-base'}`}>
          {pharmacy.name}
        </div>
        <div id="pharmacy-card-address" className={`${isMobile ? 'text-xs line-clamp-2' : 'text-sm'}`} style={{ color: '#666666' }}>
          {pharmacy.address}
        </div>
        {distance !== null && (
          <div id="pharmacy-card-distance" className={`flex items-center gap-1.5 mt-1 ${isMobile ? 'text-[10px]' : 'text-xs'}`} style={{ color: '#666666' }}>
            <TbPlane className="w-4 h-4 flex-shrink-0" />
            <span className={isMobile ? 'truncate' : ''}>{distance.toFixed(1).replace('.', ',')} {translator.translate(language, "choose_treatment", "default/km_from_you")}</span>
          </div>
        )}
        {/* Badges on desktop: under distance */}
        {!isMobile && showUnknownStockBadge && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold mt-2" style={{ backgroundColor: '#FF9800', color: '#FFFFFF' }}>
            <img src="/unKnownstockVector.png" alt={translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')} className="w-4 h-4" style={{ filter: 'brightness(0) invert(1)' }} />
            <span>{translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}</span>
          </div>
        )}
        {!isMobile && showPayOnceBadge && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold mt-2" style={{ backgroundColor: '#037D73', color: '#FFFFFF' }}>
            <span>{translator.translate(language, 'choose_treatment', 'shipping_and_service_selection/pay_once')}</span>
          </div>
        )}
      </div>

      {/* Right column on mobile: badges + cancel button; on desktop only cancel button */}
      <div className={isMobile ? 'flex flex-shrink-0 flex-col items-end gap-2' : ''}>
        {isMobile && showUnknownStockBadge && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#FF9800', color: '#FFFFFF' }}>
            <img src="/unKnownstockVector.png" alt={translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')} className="w-3.5 h-3.5 flex-shrink-0" style={{ filter: 'brightness(0) invert(1)' }} />
            <span>{translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}</span>
          </div>
        )}
        {isMobile && showPayOnceBadge && (
          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold whitespace-nowrap" style={{ backgroundColor: '#037D73', color: '#FFFFFF' }}>
            <span>{translator.translate(language, 'choose_treatment', 'shipping_and_service_selection/pay_once')}</span>
          </div>
        )}
        <button
          id="pharmacy-card-cancel-button"
          onClick={() => {
            const userId = customerState?.customerID || null;
            MainTracker.track("custom", "pharmacy_removed", {
              user_id: userId,
              pharmacy_name: pharmacy.name
            });
            onCancel();
          }}
          className={`flex items-center justify-center w-10 h-10 rounded-full cursor-pointer border-none flex-shrink-0 ${isMobile ? '' : 'ml-4'}`}
          style={{ backgroundColor: '#C6EEE4' }}
          aria-label="Cancel pharmacy selection"
        >
          <RiCloseLine className="w-6 h-6" style={{ color: '#03635C' }} />
        </button>
      </div>
    </div>
  );
};
